import { AnimationEvent } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import {
  clearFilters,
  resetFilters,
  selectResetPresetOnLogoClickEnabled,
  setSubsites,
} from '@epicuro-next/state/p-basket-state';
import { selectSubSiteId } from '@epicuro-next/state/session-state';
import { getSiteInfo, ISite } from '@epicuro-next/state/site-state';
import { isNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Site, SubSite } from 'graphql-api/gql-api-interfaces';
import { asyncScheduler, BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, shareReplay, take, takeUntil } from 'rxjs/operators';

import { siteSelectorAnimation } from '../../animations';

const SAFE_TIME_DELAY = 350;

@Component({
  selector: 'epc-brand-org-site',
  templateUrl: 'brand-org-site.component.html',
  styleUrls: ['brand-org-site.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [siteSelectorAnimation],
  standalone: false,
})
export class BrandOrgSiteComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  private destroy$ = new Subject<void>();

  @Input()
  public set selectedSite(value: Site | null) {
    this.selectedSite$.next(value);
  }

  constructor(private store: Store) {}
  @Input()
  public sites: Site[];

  @Input()
  public parentBrandOrgSelector = true;

  @Input()
  public openSelectorOnStart?: boolean;

  @Input()
  public openSelectorManually = false;

  @Input()
  public multiSiteSupport = false;

  @Input()
  public isMobile = false;

  @Output()
  public selectSite = new EventEmitter<ISite>();

  public selectedSite$: BehaviorSubject<Site | null> = new BehaviorSubject(
    null,
  );
  public isSelectedSiteNullish$ = this.selectedSite$.pipe(
    map((value) => isNullish(value)),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );
  public subSiteId$ = this.store.pipe(
    select(selectSubSiteId),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public rootPath$ = combineLatest([this.subSiteId$, this.selectedSite$]).pipe(
    map(([subSiteId, selectedSite]) => {
      if (!subSiteId) {
        return [
          '/',
          APP_TOP_LEVEL_PATH.siteSelector.custom,
          selectedSite?.siteId,
          APP_PRIVATE_PATHS.home.path,
        ];
      }

      return [
        '/',
        APP_TOP_LEVEL_PATH.siteSelector.custom,
        `${selectedSite?.siteId}:${subSiteId}`,
        APP_PRIVATE_PATHS.home.path,
      ];
    }),
    takeUntil(this.destroy$),
  );

  public siteSelectorOpened$ = new BehaviorSubject(false);

  public siteSelectorOpeningIsDone = false;

  public currentSiteBasedOnCondition$ = combineLatest([
    this.subSiteId$,
    this.selectedSite$,
  ]).pipe(
    map(([subSiteId, selectedSite]) => {
      return !!subSiteId &&
        this.sites.length === 1 &&
        this.sites[0]?.subSites?.length === 1
        ? this.sites[0]?.subSites[0]
        : selectedSite;
    }),
    takeUntil(this.destroy$),
  );

  public ngOnChanges({ openSelectorManually }: SimpleChanges) {
    if (openSelectorManually && openSelectorManually.currentValue)
      this.openSiteSelectorWithDelay();
  }

  public ngAfterViewInit() {
    if (this.openSelectorOnStart) this.openSiteSelectorWithDelay();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onAnimationDone(evt: AnimationEvent) {
    this.siteSelectorOpeningIsDone = evt.toState === 'open';
  }

  public toggleSiteSelector(value: boolean, evt?: MouseEvent) {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    this.siteSelectorOpened$.next(value);
  }

  public onSelectSiteClick(site: Site) {
    if (site.subSites?.length === 1) {
      this.onSelectedSubSiteClick(site.subSites[0] as SubSite, site);
    } else {
      this.selectSite.emit(site);
    }
    this.toggleSiteSelector(false);
  }

  public onSelectedSubSiteClick(subSite: SubSite, site: Site) {
    this.selectSite.emit(getSiteInfo(site, subSite));
    this.toggleSiteSelector(false);
  }

  public trackBySiteId(_: number, site: Site) {
    return site.siteId;
  }

  public logoClick() {
    combineLatest([
      this.store.pipe(
        select(selectResetPresetOnLogoClickEnabled),
        map(Boolean),
      ),
      this.store.pipe(select(selectSubSiteId)),
    ])
      .pipe(take(1))
      .subscribe(([resetPreset, subSiteId]: [boolean, string | null]) => {
        if (resetPreset) {
          this.store.dispatch(clearFilters({ skipParams: true }));
        } else {
          this.store.dispatch(resetFilters());
        }
        if (subSiteId) {
          this.store.dispatch(setSubsites({ subsites: subSiteId }));
        }
      });
  }

  private openSiteSelectorWithDelay() {
    asyncScheduler.schedule(() => {
      this.siteSelectorOpened$.next(true);
    }, SAFE_TIME_DELAY);
  }
}
